import React, { useEffect, useState } from "react";
import useObjectState from "@clave/use-object-state";
import useBooleanState from "@clave/use-boolean-state";
import events from "../../events";
import usersApiClient from "../../login-register/UsersApiClient";
import { OutputControlType } from "../../types";
import style from "./edit-user-info.module.scss";
import Popup from "./Popup";
import { Checkbox } from "../../components/Checkbox";
export var ContentType;
(function (ContentType) {
    ContentType[ContentType["Password"] = 0] = "Password";
    ContentType[ContentType["Address"] = 1] = "Address";
    ContentType[ContentType["Mobile"] = 2] = "Mobile";
    ContentType[ContentType["Newsletter"] = 3] = "Newsletter";
    ContentType[ContentType["CustomerService"] = 4] = "CustomerService";
    ContentType[ContentType["Confirmation"] = 5] = "Confirmation";
})(ContentType || (ContentType = {}));
export function EditUserInfo({ user, strings, countries, customerServiceUrl, }) {
    var _a, _b, _c;
    const initUserInfo = {
        ...user,
        confirmationPassword: "",
        newPassword: "",
        repeatedNewPassword: "",
    };
    const [userInfo, setUserInfo] = useObjectState(initUserInfo);
    const initPopup = ContentType.Password;
    const [currentContent, setCurrentPopupContent] = useState(initPopup);
    const [popupState, setPopupState] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [newsletter, setNewsletter] = useBooleanState(initUserInfo.outputControl.newsletter);
    const [bidNotice, setBidNotice] = useBooleanState(initUserInfo.outputControl.bidNotice);
    const [outbidNotice, setOutbidNotice] = useBooleanState(initUserInfo.outputControl.outbidNotice);
    const [auctionCatalogue, setAuctionCatalogue] = useBooleanState(initUserInfo.auctionCatalogue);
    const [newsCatalogue, setNewsCatalogue] = useBooleanState(initUserInfo.newsCatalogue);
    const [isNewsletterDisabled, setNewsletterDisabled] = useBooleanState(false);
    const [isBidNoticeDisabled, setBidNoticeDisabled] = useBooleanState(false);
    const [isOutbidNoticeDisabled, setOutbidNoticeDisabled] = useBooleanState(false);
    const [isAuctionCatalogueDisabled, setAuctionCatalogueDisabled] = useBooleanState(false);
    const [isNewsCatalogueDisabled, setNewsCatalogueDisabled] = useBooleanState(false);
    const saveChanges = async (objectToSave, url) => {
        return usersApiClient
            .update(url, objectToSave)
            .then((data) => {
            if (data != null) {
                setUserInfo({
                    ...initUserInfo,
                    ...data,
                });
                setErrorMessage("");
                setCurrentPopupContent(ContentType.Confirmation);
                return true;
            }
        })
            .catch((err) => {
            var _a;
            if (err.status === 401) {
                setErrorMessage("Feil passord");
            }
            else if (err.status === 400) {
                setErrorMessage((_a = err.data[0]) === null || _a === void 0 ? void 0 : _a.errorMessage);
            }
            else {
                setErrorMessage(err.data.message);
            }
            return false;
        });
    };
    const openEditPopup = (type) => {
        setCurrentPopupContent(type);
        setErrorMessage("");
        setPopupState(true);
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
        events.setBackdrop.trigger(true);
    };
    const closeEditPopup = () => {
        setCurrentPopupContent(initPopup);
        setPopupState(false);
        events.setBackdrop.trigger(false);
    };
    async function updateOutputControl(type, payload, disable) {
        disable.toTrue();
        await saveChanges({ payload: payload, outputControlType: type }, "/update/outputControl");
        setUserInfo.set("outputControl", { newsletter: newsletter, bidNotice: bidNotice, outbidNotice: outbidNotice });
        disable.toFalse();
    }
    useEffect(() => {
        if (newsletter != userInfo.outputControl.newsletter) {
            updateOutputControl(OutputControlType.Newsletter, newsletter, setNewsletterDisabled);
        }
    }, [newsletter]);
    useEffect(() => {
        if (bidNotice != userInfo.outputControl.bidNotice) {
            updateOutputControl(OutputControlType.BidNotice, bidNotice, setBidNoticeDisabled);
        }
    }, [bidNotice]);
    useEffect(() => {
        if (outbidNotice != userInfo.outputControl.outbidNotice) {
            updateOutputControl(OutputControlType.OutbidNotice, outbidNotice, setOutbidNoticeDisabled);
        }
    }, [outbidNotice]);
    async function updateProperty(object, disable) {
        disable.toTrue();
        await saveChanges(object, "/update/property");
        setUserInfo.set("auctionCatalogue", auctionCatalogue);
        disable.toFalse();
    }
    async function saveProperty(object) {
        await saveChanges(object, "/update/property");
    }
    useEffect(() => {
        if (auctionCatalogue != userInfo.auctionCatalogue) {
            setAuctionCatalogueDisabled.toTrue();
            saveChanges({ auctionCatalogue: auctionCatalogue }, "/update/property").then(() => {
                setUserInfo.set("auctionCatalogue", auctionCatalogue);
                setAuctionCatalogueDisabled.toFalse();
            });
        }
    }, [auctionCatalogue]);
    useEffect(() => {
        if (newsCatalogue != userInfo.newsCatalogue) {
            setNewsCatalogueDisabled.toTrue();
            saveChanges({ newsCatalogue: newsCatalogue }, "/update/property").then(() => {
                setUserInfo.set("newsCatalogue", newsCatalogue);
                setNewsCatalogueDisabled.toFalse();
            });
        }
    }, [newsCatalogue]);
    const defineInvoiceAddressText = () => {
        if (userInfo.billingAddress == null) {
            return "Fakturaadresse er den samme som leveringsadressen";
        }
        else {
            return `${userInfo.billingAddress.line1} ${userInfo.billingAddress.postalCode} ${userInfo.billingAddress.city}`;
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: style.wrapper },
            React.createElement("div", { className: style.userInfo },
                React.createElement("div", { className: style.userText },
                    React.createElement("span", { className: style.highlight },
                        strings.customerNumber,
                        ":"),
                    React.createElement("span", null, userInfo.currentRambaseId))),
            React.createElement("div", { className: style.userInfo },
                React.createElement("div", { className: style.userText },
                    React.createElement("span", { className: style.highlight },
                        strings.name,
                        ":"),
                    React.createElement("span", null,
                        userInfo.firstName,
                        " ",
                        userInfo.lastName)),
                React.createElement("button", { className: `${style.userAction} btn primary`, onClick: () => openEditPopup(ContentType.CustomerService) }, strings.edit)),
            React.createElement("div", { className: style.userInfo },
                React.createElement("div", { className: style.userText },
                    React.createElement("span", { className: style.highlight },
                        strings.mobile,
                        ":"),
                    React.createElement("span", null, userInfo.mobilePhoneNumber)),
                React.createElement("button", { className: `${style.userAction} btn primary`, onClick: () => openEditPopup(ContentType.Mobile) }, strings.edit)),
            React.createElement("div", { className: style.userInfo },
                React.createElement("div", { className: style.userText },
                    React.createElement("span", { className: style.highlight },
                        strings.email,
                        ":"),
                    React.createElement("span", null, userInfo.email)),
                React.createElement("button", { className: `${style.userAction} btn primary`, onClick: () => openEditPopup(ContentType.CustomerService) }, strings.edit)),
            React.createElement("div", { className: style.userInfo },
                React.createElement("div", { className: style.userText },
                    React.createElement("span", { className: style.highlight },
                        strings.password,
                        ":"),
                    React.createElement("span", null, "***********")),
                React.createElement("button", { className: `${style.userAction} btn primary`, onClick: () => openEditPopup(ContentType.Password) }, strings.edit)),
            React.createElement("div", { className: style.userInfo },
                React.createElement("div", { className: style.userText },
                    React.createElement("span", { className: style.highlight },
                        strings.deliveryAddress,
                        ":"),
                    React.createElement("span", null, (_a = userInfo.deliveryAddress) === null || _a === void 0 ? void 0 :
                        _a.line1,
                        " ", (_b = userInfo.deliveryAddress) === null || _b === void 0 ? void 0 :
                        _b.postalCode,
                        " ", (_c = userInfo.deliveryAddress) === null || _c === void 0 ? void 0 :
                        _c.city)),
                React.createElement("button", { className: `${style.userAction} btn primary`, onClick: () => openEditPopup(ContentType.Address) }, strings.edit)),
            React.createElement("div", { className: style.userInfo },
                React.createElement("div", { className: style.userText },
                    React.createElement("span", { className: style.highlight },
                        strings.billingAddress,
                        ":"),
                    React.createElement("span", null, defineInvoiceAddressText())),
                React.createElement("button", { className: `${style.userAction} btn primary`, onClick: () => openEditPopup(ContentType.CustomerService) }, strings.edit)),
            React.createElement("div", { className: style.userInfo },
                React.createElement("div", { className: style.userText },
                    React.createElement("span", { className: style.highlight },
                        strings.shippingServiceNameShop,
                        ":"),
                    React.createElement("span", null, userInfo.shippingServiceNameShop)),
                React.createElement("button", { className: `${style.userAction} btn primary`, onClick: () => openEditPopup(ContentType.CustomerService) }, strings.edit)),
            React.createElement("div", { className: style.userInfo },
                React.createElement("div", { className: style.userText },
                    React.createElement("span", { className: style.highlight },
                        strings.shippingServiceNameAuction,
                        ":"),
                    React.createElement("span", null, userInfo.shippingServiceNameAuction)),
                React.createElement("button", { className: `${style.userAction} btn primary`, onClick: () => openEditPopup(ContentType.CustomerService) }, strings.edit)),
            React.createElement("h4", { className: style.sectionTitle },
                " ",
                strings.emailsSection,
                " "),
            React.createElement("div", { className: style.userInfo },
                React.createElement("div", { className: style.userText },
                    React.createElement(Checkbox, { checked: newsletter, setChecked: setNewsletter, disabled: isNewsletterDisabled }),
                    React.createElement("span", { className: style.checkboxTitle }, strings.newsletterTitle),
                    React.createElement("span", null, strings.newsletterDescription))),
            React.createElement("div", { className: style.userInfo },
                React.createElement("div", { className: style.userText },
                    React.createElement(Checkbox, { checked: bidNotice, setChecked: setBidNotice, disabled: isBidNoticeDisabled }),
                    React.createElement("span", { className: style.checkboxTitle }, strings.bidNoticeTitle),
                    React.createElement("span", null, strings.bidNoticeDescription))),
            React.createElement("div", { className: style.userInfo },
                React.createElement("div", { className: style.userText },
                    React.createElement(Checkbox, { checked: outbidNotice, setChecked: setOutbidNotice, disabled: isOutbidNoticeDisabled }),
                    React.createElement("span", { className: style.checkboxTitle }, strings.outbidNoticeTitle),
                    React.createElement("span", null, strings.outbidNoticeDescription))),
            React.createElement("h4", { className: style.sectionTitle },
                " ",
                strings.cataloguesSection,
                " "),
            React.createElement("div", { className: style.userInfo },
                React.createElement("div", { className: style.userText },
                    React.createElement(Checkbox, { checked: auctionCatalogue, setChecked: setAuctionCatalogue, disabled: isAuctionCatalogueDisabled }),
                    React.createElement("span", { className: style.checkboxTitle }, strings.auctionCatalogueTitle),
                    React.createElement("span", null, strings.auctionCatalogueDescription))),
            React.createElement("div", { className: style.userInfo },
                React.createElement("div", { className: style.userText },
                    React.createElement(Checkbox, { checked: newsCatalogue, setChecked: setNewsCatalogue, disabled: isNewsCatalogueDisabled }),
                    React.createElement("span", { className: style.checkboxTitle }, strings.newsCatalogueTitle),
                    React.createElement("span", null, strings.newsCatalogueDescription)))),
        React.createElement(Popup, { strings: strings, contentType: currentContent, userInfo: userInfo, save: saveChanges, countries: countries, open: popupState, close: closeEditPopup, customerServiceUrl: customerServiceUrl },
            React.createElement("div", { className: "error show" },
                React.createElement("span", null, errorMessage)))));
}
